import React, { useEffect, useState, useMemo, useRef } from 'react';
import { pollDeviceAuthSession, startDeviceAuthSession } from '../connections/fetch-stream';
import { logBrowserInfo, checkStreamSupport, getBrowserInfo } from '../utils/browser-compatibility';
import ConsoleOverlay from '../components/console-overlay';
import { getEnv } from '../utils/env';

// defined in the api, in v4/access/auth.service.ts startDeviceAuth()
const expirationInMs = 1000 * 60 * 60;
// 15 seconds before the code expires, refresh the code
const codeIsValidFor = expirationInMs - 1000 * 15;

declare global {
  interface Window {
    synergysky?: SynergySky;
  }
}

interface SynergySky {
  startSession: (version: string, appId: string) => Promise<void>;
}

const ConferenceAuth: React.FC = () => {
  const { VITE_STAGE, VITE_GREENROOM_ASSETS } = getEnv();
  const [uniqueCode, setUniqueCode] = useState('');
  const [magicLink, setMagicLink] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [streamStatus, setStreamStatus] = useState<'connecting' | 'connected' | 'error' | 'disconnected'>('connecting');
  const [browserInfo] = useState(getBrowserInfo());
  //const [authToken, setAuthToken] = useState<string | null>(null);
  const isLowerEnv = VITE_STAGE === 'qa' || VITE_STAGE === 'dev';
  // Log browser compatibility info on component mount
  useEffect(() => {
    logBrowserInfo();
    const supportsStreams = checkStreamSupport();
    if (!supportsStreams) {
      console.error('ConferenceAuth: Browser does not support required stream features');
      setError('Browser compatibility issue detected');
    }
  }, []);

  // Conference auth needs to initialize the Synergy Sky session
  // in case user stays on page for extended period of time
  const loadSynergySky = async () => {
    const isSynergySkySession = (window as Window).synergysky;
    if (isSynergySkySession) {
      await (window as Window).synergysky?.startSession('1.0', 'greenroom-qa');
    }
  };

  useEffect(() => {
    loadSynergySky();
  }, []);

  const pollingTimeout = useRef<NodeJS.Timeout | null>(null);

  const initializeDeviceAuthSession = async () => {
    setStreamStatus('connected');

    startDeviceAuthSession()
      .then((data) => {
        console.log('ConferenceAuth: Data from startDeviceAuthSession', data);
        // uniqueCode,
        // expiresIn: expiration,
        // message: 'Device auth session started'
        setUniqueCode(data.uniqueCode);
        //setAuthToken(data.token || null);
        setStreamStatus('connected');
        setIsLoading(false);

        pollingTimeout.current = setInterval(() => {
          console.log('ConferenceAuth: Polling for unique code');
          pollDeviceAuthSession(data.uniqueCode, data.authKey)
            .then((pollData) => {
              if (pollData.status === 'completed' && pollData.type === 'magic-link') {
                setMagicLink(pollData.data);
              }
            })
            .catch((pollError) => {
              console.error('ConferenceAuth: Polling error', pollError);
              setError(`Polling error: ${pollError.message}`);
            });
        }, 5000);
      })
      .catch((error) => {
        console.error('ConferenceAuth: Error starting device auth session', error);
        setError(`Session error: ${error.message}`);
        setStreamStatus('error');
        setIsLoading(false);
      });
  };

  useEffect(() => {
    console.log('ConferenceAuth: Starting stream connection...');
    setIsLoading(true);
    setError(null);

    initializeDeviceAuthSession();
    // Cleanup polling timeout on unmount or refresh
    return () => {
      if (pollingTimeout.current) {
        clearInterval(pollingTimeout.current);
        pollingTimeout.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      console.log('ConferenceAuth: Code expired, refreshing stream');
      initializeDeviceAuthSession();
    }, codeIsValidFor);
    return () => {
      clearTimeout(timeout);
    };
  }, [uniqueCode]);

  useEffect(() => {
    if (magicLink) {
      console.log('ConferenceAuth: Magic link received, redirecting');
      window.sessionStorage.setItem('limitedInputAuth', 'true');
      window.location.href = `${magicLink}`;
    }
  }, [magicLink]);

  const codeArray = useMemo(() => uniqueCode?.split('') ?? [], [uniqueCode]);

  // Debug logging for code display
  useEffect(() => {
    console.log('ConferenceAuth: Code state updated', {
      uniqueCode,
      codeArray,
      codeArrayLength: codeArray.length,
      hasValidCode: codeArray.length === 5 && codeArray.every((char) => char && char.length === 1),
    });
  }, [uniqueCode, codeArray]);

  // Check for potential issues
  const hasValidCode = codeArray.length === 5 && codeArray.every((char) => char && char.length === 1);
  const showError = error || streamStatus === 'error';
  const showLoading = isLoading || streamStatus === 'connecting';

  return (
    <div className="auth-code-container">
      <div className="centered-wrapper">
        <img
          className="green-lower-gradient"
          src={`${VITE_GREENROOM_ASSETS}/gradients/auth-code-screen/gradient.webp`}
          alt="lower left gradient"
        />

        <div className="two-column-layout">
          <div className="bottom-text-container">
            <h1 className="solid-green-text">
              Greenroom for<br></br> Conference Rooms.
            </h1>
            <h4 className="sub-text">
              Scan the QR code or visit <span className="large-white">greenroom.brandlive.com</span>
              <br></br> and input the following code
            </h4>

            {/* Error state */}
            {isLowerEnv && showError && (
              <div
                style={{
                  color: '#ff6961',
                  textAlign: 'center',
                  marginTop: '20px',
                  padding: '10px',
                  backgroundColor: 'rgba(255, 105, 97, 0.1)',
                  borderRadius: '8px',
                }}
              >
                <p style={{ margin: '0 0 10px 0', fontWeight: 'bold' }}>Connection Issue</p>
                <p style={{ margin: '0 0 10px 0', fontSize: '14px' }}>{error}</p>
                <button
                  onClick={() => window.location.reload()}
                  style={{
                    background: '#5ae4ff',
                    color: '#000',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                  }}
                >
                  Refresh Page
                </button>
              </div>
            )}

            {/* Loading state */}
            {isLowerEnv && showLoading && !showError && (
              <div
                style={{
                  color: '#fff',
                  textAlign: 'center',
                  marginTop: '20px',
                  padding: '10px',
                }}
              >
                <p style={{ margin: '0', fontSize: '16px' }}>Connecting to server...</p>
              </div>
            )}

            {/* Code display */}
            {!showError && (
              <div className="code-text">
                <div className="code-block">{codeArray[0] || ''}</div>
                <div className="code-block">{codeArray[1] || ''}</div>
                <div className="code-block">{codeArray[2] || ''}</div>
                <div className="code-block">{codeArray[3] || ''}</div>
                <div className="code-block">{codeArray[4] || ''}</div>
              </div>
            )}
            <img
              width={`114px`}
              className="bl-logo"
              height={`40px`}
              src={`${VITE_GREENROOM_ASSETS}/logos/bl-logo.svg`}
            />

            {/* Debug info for development */}
            {isLowerEnv && (
              <div
                style={{
                  position: 'fixed',
                  top: '10px',
                  right: '10px',
                  background: 'rgba(0,0,0,0.8)',
                  color: '#fff',
                  padding: '10px',
                  fontSize: '12px',
                  borderRadius: '4px',
                  maxWidth: '200px',
                }}
              >
                <div>Status: {streamStatus}</div>
                <div>Code: {uniqueCode || 'none'}</div>
                <div>Valid: {hasValidCode ? 'yes' : 'no'}</div>
                <div>Loading: {isLoading ? 'yes' : 'no'}</div>
                <div>Cisco: {browserInfo.isCisco ? 'yes' : 'no'}</div>
                <div>Streams: {browserInfo.supportsStreams ? 'yes' : 'no'}</div>
              </div>
            )}
          </div>

          <div className="side-qr-container">
            <img src={`${VITE_GREENROOM_ASSETS}/qr-codes/qr-code.webp`} width="338.314px" height="338.314px" />
          </div>
        </div>

        {/* Console Overlay for debugging */}
        {isLowerEnv && <ConsoleOverlay isVisible={true} maxLogs={200} showTimestamp={true} showLevel={true} />}
      </div>
    </div>
  );
};

export default ConferenceAuth;
